import { optionalProtectedResourceLoader, protectedResourceAction } from '~/shell/utils/authUtil.server';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { data, useFetcher, useLocation, useNavigate, useSearchParams } from 'react-router';
import { getSalesOrderSetFilter, setSalesOrderSetFilter } from '~/shell/utils/salesOrdersetFilterUtil';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useLoginInfo } from '~/shell/hooks/useLoginInfo';
import { useBasePath } from '~/common/hooks/useBasePath';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_SEARCH_PARAM_PAGE } from '~/features/catalog/mappers/catalogSearchQueryMapper';

export const loader = optionalProtectedResourceLoader(async ({ request, loginInfo }) => {
  let isActive = false;
  const isFilterAvailable = loginInfo?.hasPurchasedArticles ?? false;
  if (isFilterAvailable) {
    isActive = await getSalesOrderSetFilter(request);
  }

  return {
    isFilterAvailable,
    isActive
  };
});

export const action = protectedResourceAction(async ({ request }) => {
  const headers = new Headers();

  const formData = await request.formData();
  const value = formData.get(FORM_FIELDS.active);
  const isActive = value === 'true';

  await setSalesOrderSetFilter(isActive, headers);

  return data({ active: isActive }, { headers });
});

export function useSalesordersetFilter() {
  const { loginInfo, isSalesOrderSetFilterActive } = useLoginInfo();
  const { load, data, state } = useFetcher<typeof loader>();
  const fetcher = useFetcher<typeof action>();
  const { i18n } = useTranslation();
  const [optimisticActive, setOptimisticActive] = useState<boolean>(isSalesOrderSetFilterActive);
  const [isFilterAvailable, setIsFilterAvailable] = useState<boolean>(loginInfo?.hasPurchasedArticles ?? false);
  const { path } = useBasePath();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isOnCatalogPage = location.pathname.endsWith('/catalog');

  const setSalesOrdersetFilter = async (active: boolean) => {
    queryClient.clear();

    setOptimisticActive(active);

    const formData = new FormData();
    formData.append(FORM_FIELDS.active, active.toString());
    await fetcher.submit(formData, {
      method: 'post',
      action: `${path}/resources/articles/salesordersetfilter`
    });

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(QUERY_SEARCH_PARAM_PAGE, '0');
    navigate(`${path}/catalog?${newSearchParams.toString()}`, { replace: isOnCatalogPage });
  };

  useEffect(() => {
    load(`${path}/resources/articles/salesordersetfilter`);
  }, [i18n.language, load, path]);

  useEffect(() => {
    if (data) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOptimisticActive(data.isActive);
      setIsFilterAvailable(data.isFilterAvailable);
    }
  }, [data]);

  return {
    isFilterAvailable: isFilterAvailable,
    isActive: optimisticActive,
    isLoading: state === 'loading' || fetcher.state === 'submitting',
    setSalesOrdersetFilter
  };
}
